import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Shimmer from './Shimmer';

// Skeleton for the home-page SpecialsStrip. Mirrors the real block 1:1:
//   white section · forest top border · blinking-dot eyebrow + big serif
//   campaign title + "Ends …" line on the left; 3 campaign pills + "View
//   all offers" button on the right; 4-col card grid below with each
//   card having a forest top stripe + emoji + title + description +
//   price row + save pill footer.

const OfferCardShimmer = () => (
  <Box
    sx={{
      bgcolor: 'var(--cream)',
      border: '1.5px solid var(--border-l)',
      borderRadius: '18px',
      p: { xs: 2, md: 2.5 },
      pt: { xs: 2.25, md: 2.75 },
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      // Forest top stripe — matches the real card.
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: 6,
        bgcolor: 'var(--forest)',
        borderTopLeftRadius: '18px',
        borderTopRightRadius: '18px',
      },
    }}
  >
    <Shimmer width={34} height={28} rounded={6} sx={{ mb: 1.5 }} />
    <Shimmer width="90%" height={14} rounded={4} sx={{ mb: 0.75 }} />
    <Shimmer width="60%" height={14} rounded={4} sx={{ mb: 1.5 }} />
    <Shimmer width="95%" height={11} rounded={4} sx={{ mb: 0.5 }} />
    <Shimmer width="75%" height={11} rounded={4} sx={{ mb: 'auto' }} />
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 2, mb: 1.25 }}>
      <Shimmer width={90} height={24} rounded={6} />
      <Shimmer width={54} height={11} rounded={4} />
    </Stack>
    <Shimmer width="100%" height={26} rounded={100} />
  </Box>
);

const HomeSpecialsStripShimmer = ({ cards = 4 }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      py: { xs: 5, md: 7 },
      borderTop: '4px solid var(--forest)',
      borderBottom: '1px solid var(--border-l)',
    }}
  >
    <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
      {/* Header row */}
      <Grid container columns={12} columnSpacing={2} rowSpacing={2} alignItems="center" sx={{ mb: 3.5 }}>
        {/* Left: eyebrow + campaign title + date line */}
        <Grid item xs={12} md={7}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                bgcolor: '#25D366',
                borderRadius: '50%',
                opacity: 0.6,
                flexShrink: 0,
              }}
            />
            <Shimmer width={130} height={10} rounded={4} />
          </Stack>
          <Shimmer width="55%" height={30} rounded={6} sx={{ mb: 1 }} />
          <Shimmer width={280} height={11} rounded={4} />
        </Grid>

        {/* Right: campaign pills + "View all offers" CTA */}
        <Grid item xs={12} md={5} sx={{ marginLeft: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: 1,
              width: '100%',
            }}
          >
            {Array.from({ length: 3 }).map((_, i) => (
              <Shimmer key={i} width={80 + i * 14} height={30} rounded={100} />
            ))}
            <Shimmer width={130} height={30} rounded={100} />
          </Box>
        </Grid>
      </Grid>

      {/* Card grid — fixed 4-col on md so it matches the real layout. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
          width: '100%',
        }}
      >
        {Array.from({ length: cards }).map((_, i) => <OfferCardShimmer key={i} />)}
      </Box>
    </Container>
  </Box>
);

export default HomeSpecialsStripShimmer;
